// Web equivalent of the native biometric login (see ./biometric.ts).
//
// Native path stores the refresh token in the iOS Keychain / Android Keystore and
// uses Face ID / Touch ID to unlock it. The browser has no Keychain, so instead we:
//   1. Create a platform WebAuthn credential (Touch ID / Face ID / Windows Hello /
//      Android fingerprint) with the PRF extension enabled.
//   2. Ask the authenticator to derive a stable per-credential secret (PRF output),
//      gated by a biometric/user-verification prompt.
//   3. Use that secret as an AES-GCM key to encrypt the refresh token before it ever
//      touches localStorage. The key itself never leaves the secure authenticator.
//
// Unlock re-runs the biometric prompt, re-derives the same PRF secret, and decrypts
// the stored token — then auth.store exchanges it via /refresh, exactly like native.
//
// Hard requirement: the PRF extension. Supported on Chrome/Edge 132+, Safari 18+
// (iOS 18 / macOS 15), and Android. Without it the feature self-disables — we never
// fall back to storing the token in the clear.

// localStorage keys. Only the opt-in flag and the *encrypted* token live here.
const WEB_BIO_FLAG = 'WEB_BIOMETRIC_ENABLED';
const WEB_BIO_CRED = 'webauthn.credentialId'; // base64url credential id
const WEB_BIO_BLOB = 'webauthn.encToken'; // JSON: { iv, ct } (base64url)

// Fixed, app-specific salt fed to the PRF. Same salt -> same derived secret, so the
// key is reproducible across sessions on this device. Not a secret itself.
const PRF_SALT = new TextEncoder().encode('vessel-haven:web-biometric:v1');

// --- base64url <-> ArrayBuffer helpers (no padding, URL-safe) -----------------

const bufToB64url = (buf: ArrayBuffer): string => {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

const b64urlToBuf = (b64url: string): ArrayBuffer => {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const bin = window.atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
};

const randomBytes = (len: number): Uint8Array<ArrayBuffer> =>
  window.crypto.getRandomValues(new Uint8Array(len));

// PRF re-derivation requires a biometric prompt, but token rotation happens silently
// mid-session (api.ts interceptor). Cache the secret in memory after the first
// enable/unlock so rotation can re-encrypt without re-prompting. Never persisted.
let sessionSecret: ArrayBuffer | null = null;

// PRF extension is not in the stock lib.dom typings yet — narrow casts isolate it here.
type PrfOutputs = { enabled?: boolean; results?: { first?: ArrayBuffer } };
const readPrf = (cred: PublicKeyCredential): PrfOutputs =>
  (cred.getClientExtensionResults() as { prf?: PrfOutputs }).prf ?? {};

const rpId = (): string => window.location.hostname;

// --- capability checks --------------------------------------------------------

/** WebAuthn present AND a platform authenticator (built-in biometrics) is available. */
export const canUseWebBiometric = async (): Promise<boolean> => {
  try {
    if (
      typeof window === 'undefined' ||
      !window.PublicKeyCredential ||
      !window.isSecureContext ||
      typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable !== 'function'
    ) {
      return false;
    }
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
};

export const isWebBiometricEnabled = (): boolean =>
  window.localStorage.getItem(WEB_BIO_FLAG) === 'true' &&
  !!window.localStorage.getItem(WEB_BIO_CRED) &&
  !!window.localStorage.getItem(WEB_BIO_BLOB);

// --- crypto -------------------------------------------------------------------

// PRF "first" output is 32 bytes — import it straight as an AES-256-GCM key.
const importAesKey = (secret: ArrayBuffer): Promise<CryptoKey> =>
  window.crypto.subtle.importKey('raw', secret, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);

const encryptToken = async (secret: ArrayBuffer, token: string): Promise<void> => {
  const key = await importAesKey(secret);
  const iv = randomBytes(12);
  const ct = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(token),
  );
  window.localStorage.setItem(
    WEB_BIO_BLOB,
    JSON.stringify({ iv: bufToB64url(iv.buffer), ct: bufToB64url(ct) }),
  );
};

const decryptToken = async (secret: ArrayBuffer): Promise<string | null> => {
  const raw = window.localStorage.getItem(WEB_BIO_BLOB);
  if (!raw) return null;
  try {
    const { iv, ct } = JSON.parse(raw) as { iv: string; ct: string };
    const key = await importAesKey(secret);
    const plain = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: new Uint8Array(b64urlToBuf(iv)) },
      key,
      b64urlToBuf(ct),
    );
    return new TextDecoder().decode(plain);
  } catch {
    return null;
  }
};

// --- public API (mirrors ./biometric.ts) --------------------------------------

/**
 * Register a platform credential and stash the refresh token encrypted under a
 * biometric-gated PRF secret. Must be called from a user gesture (the browser
 * requires transient activation for credential creation). Throws if PRF is
 * unsupported so the caller can keep the feature off rather than store in clear.
 */
export const enableWebBiometricLogin = async (
  refreshToken: string,
  userName = 'Vessel Haven user',
): Promise<void> => {
  const cred = (await navigator.credentials.create({
    publicKey: {
      challenge: randomBytes(32), // local-only ceremony — challenge is never server-verified
      rp: { name: 'Vessel Haven', id: rpId() },
      user: { id: randomBytes(16), name: userName, displayName: userName },
      // ES256 then RS256 — covers every platform authenticator.
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 },
      ],
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        userVerification: 'required',
        residentKey: 'preferred',
      },
      timeout: 60_000,
      // Ask for the PRF secret up front (Chrome 132+/Safari 18 return it at creation).
      extensions: { prf: { eval: { first: PRF_SALT } } } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!cred) throw new Error('Credential creation returned null');

  const prf = readPrf(cred);
  if (!prf.enabled) {
    throw new Error('PRF extension unsupported on this authenticator');
  }

  window.localStorage.setItem(WEB_BIO_CRED, bufToB64url(cred.rawId));

  // Most browsers hand back the PRF result right here. If not, do one follow-up
  // assertion (still inside the same gesture) to obtain it.
  let secret = prf.results?.first;
  if (!secret) {
    secret = (await deriveSecret(cred.rawId)) ?? undefined;
  }
  if (!secret) {
    window.localStorage.removeItem(WEB_BIO_CRED);
    throw new Error('Could not derive PRF secret');
  }

  sessionSecret = secret;
  await encryptToken(secret, refreshToken);
  window.localStorage.setItem(WEB_BIO_FLAG, 'true');
};

// Run an assertion against the stored credential and return its PRF "first" output.
const deriveSecret = async (credId: ArrayBuffer): Promise<ArrayBuffer | null> => {
  const assertion = (await navigator.credentials.get({
    publicKey: {
      challenge: randomBytes(32),
      rpId: rpId(),
      allowCredentials: [{ id: credId, type: 'public-key', transports: ['internal'] }],
      userVerification: 'required',
      timeout: 60_000,
      extensions: { prf: { eval: { first: PRF_SALT } } } as AuthenticationExtensionsClientInputs,
    },
  })) as PublicKeyCredential | null;

  if (!assertion) return null;
  return readPrf(assertion).results?.first ?? null;
};

/**
 * Prompt biometrics, re-derive the PRF secret, decrypt and return the refresh token.
 * Returns null on cancel/failure. Must be called from a user gesture.
 */
export const webBiometricUnlock = async (): Promise<string | null> => {
  const credB64 = window.localStorage.getItem(WEB_BIO_CRED);
  if (!credB64) return null;
  try {
    const secret = await deriveSecret(b64urlToBuf(credB64));
    if (!secret) return null;
    sessionSecret = secret;
    return await decryptToken(secret);
  } catch {
    // user cancelled, no matching credential, or PRF unavailable
    return null;
  }
};

/**
 * Re-encrypt the stored refresh token after backend rotation. Silent: reuses the
 * in-memory PRF secret cached at enable/unlock — never prompts. No-op unless enabled
 * and a secret has been derived this session (otherwise the stored token goes stale
 * and the next unlock simply falls back to password).
 */
export const refreshStoredWebToken = async (refreshToken: string): Promise<void> => {
  if (!refreshToken || !isWebBiometricEnabled() || !sessionSecret) return;
  try {
    await encryptToken(sessionSecret, refreshToken);
  } catch (err) {
    console.error('Failed to update stored web biometric token:', err);
  }
};

/** Wipe stored credential + encrypted token and clear the opt-in flag. */
export const disableWebBiometricLogin = async (): Promise<void> => {
  sessionSecret = null;
  window.localStorage.removeItem(WEB_BIO_FLAG);
  window.localStorage.removeItem(WEB_BIO_CRED);
  window.localStorage.removeItem(WEB_BIO_BLOB);
};
